import { useState, useEffect } from "react";
import axios from "axios";
import proxy from "../proxy";
import {
  Box,
  Text,
  HStack,
  VStack,
  Badge,
  Skeleton,
} from "@chakra-ui/react";

// Raportul e salvat cu paragrafele separate prin linie goală (spre deosebire de
// DailyInsight/WeeklyCoach, care sunt un singur bloc de text).
const splitParagraphs = (text) =>
  String(text || "")
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);

const NextBuy = () => {
  const [pick, setPick] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${proxy}server_mts/GetNextBuy.php`, {
        headers: { "Cache-Control": "no-cache" },
      })
      .then((res) => {
        if (res.data?.ticker && res.data?.report_text) {
          setPick(res.data);
        }
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, []);

  if (isLoading) {
    return (
      <Box
        maxW="900px"
        mx="auto"
        p={{ base: 3, md: 4 }}
        bg="rgba(45, 55, 72, 0.5)"
        borderRadius="xl"
        border="1px solid"
        borderColor="whiteAlpha.100"
      >
        <Skeleton height="14px" mb={2} startColor="whiteAlpha.100" endColor="whiteAlpha.200" />
        <Skeleton height="14px" mb={2} startColor="whiteAlpha.100" endColor="whiteAlpha.200" />
        <Skeleton height="14px" width="70%" startColor="whiteAlpha.100" endColor="whiteAlpha.200" />
      </Box>
    );
  }

  if (!pick) return null;

  const date = new Date(pick.created_at);
  const formattedDate = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  const daysAgo = Math.floor((new Date() - date) / (1000 * 60 * 60 * 24));
  const timeLabel = daysAgo === 0 ? "Today" : daysAgo === 1 ? "Yesterday" : `${daysAgo}d ago`;

  const paragraphs = splitParagraphs(pick.report_text);

  return (
    <Box maxW="900px" mx="auto" position="relative" overflow="hidden">
      <Box
        p={{ base: 3, md: 4 }}
        bg="linear-gradient(135deg, rgba(159, 122, 234, 0.08), rgba(66, 153, 225, 0.08))"
        borderRadius="xl"
        border="1px solid"
        borderColor="rgba(159, 122, 234, 0.2)"
        backdropFilter="blur(10px)"
        transition="all 0.3s"
        _hover={{
          borderColor: "rgba(159, 122, 234, 0.4)",
          boxShadow: "0 8px 30px rgba(159, 122, 234, 0.15)",
        }}
      >
        {/* Purple gradient top accent */}
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          height="2px"
          bgGradient="linear(to-r, purple.400, blue.400)"
          opacity={0.6}
        />

        <VStack spacing={{ base: 2, md: 2.5 }} align="stretch">
          {/* Header */}
          <HStack justify="space-between" flexWrap="wrap" gap={2}>
            <HStack spacing={2}>
              <Badge
                bg="linear-gradient(135deg, #9F7AEA, #6B46C1)"
                color="white"
                fontSize={{ base: "2xs", md: "xs" }}
                px={2}
                py={0.5}
                borderRadius="full"
                fontWeight="bold"
              >
                🛒 Next Buy
              </Badge>
              <Text fontSize={{ base: "2xs", md: "xs" }} color="gray.500">
                {formattedDate} • {timeLabel}
              </Text>
            </HStack>

            <HStack spacing={2}>
              {pick.runner_up && (
                <Badge
                  colorScheme="blue"
                  fontSize={{ base: "2xs", md: "xs" }}
                  px={2}
                  py={0.5}
                  borderRadius="full"
                  variant="subtle"
                >
                  runner-up {pick.runner_up}
                </Badge>
              )}
              {pick.allocation && (
                <Badge
                  colorScheme="purple"
                  fontSize={{ base: "2xs", md: "xs" }}
                  px={2}
                  py={0.5}
                  borderRadius="full"
                  variant="subtle"
                >
                  {pick.allocation}
                </Badge>
              )}
            </HStack>
          </HStack>

          {/* Tickerul recomandat + motivul într-o propoziție */}
          <HStack align="flex-start" spacing={3}>
            <VStack
              spacing={0}
              px={3}
              py={2}
              borderRadius="lg"
              bg="rgba(159, 122, 234, 0.15)"
              border="1px solid"
              borderColor="rgba(159, 122, 234, 0.3)"
              minW="72px"
            >
              <Text fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" color="purple.200" lineHeight={1.2}>
                {pick.ticker}
              </Text>
              <Text fontSize="2xs" color="gray.500">
                next buy
              </Text>
            </VStack>

            <VStack align="start" spacing={0.5} flex={1}>
              <Text fontSize={{ base: "xs", md: "sm" }} color="gray.400">
                {pick.company}
              </Text>
              <Text
                fontSize={{ base: "sm", md: "md" }}
                color="gray.100"
                fontWeight="semibold"
                lineHeight="short"
              >
                {pick.headline}
              </Text>
            </VStack>
          </HStack>

          {/* Raportul */}
          <VStack align="stretch" spacing={2}>
            {paragraphs.map((p, i) => (
              <Text
                key={i}
                fontSize={{ base: "xs", md: "sm" }}
                color="gray.300"
                lineHeight="tall"
              >
                {p}
              </Text>
            ))}
          </VStack>

          {/* Footer */}
          <HStack spacing={1} justify="flex-end">
            <Text fontSize="2xs" color="gray.600" fontStyle="italic">
              Powered by Gemini AI • Generated on demand, not investment advice
            </Text>
          </HStack>
        </VStack>
      </Box>
    </Box>
  );
};

export default NextBuy;
